import { useState } from 'react';

export interface SliderProps {
  /** Current value as percentage (0-100) */
  value: number;
  /** Reference/saved value as percentage (0-100), shown as grey dot */
  referenceValue?: number;
  /** Called when value changes */
  onChange: (value: number) => void;
  /** Whether the slider is disabled */
  disabled?: boolean;
  /** Step size for discrete values (e.g., 25 for 5 positions). Omit for continuous. */
  step?: number;
  /** Called when drag starts */
  onDragStart?: () => void;
  /** Called when drag ends */
  onDragEnd?: () => void;
}

/**
 * Slider component with reference value indicator.
 *
 * Features:
 * - Grey dot shows reference/saved value (only when different from current)
 * - Cobalt (#290BD9) track fill and thumb for current value
 * - Invisible range input overlay for smooth dragging
 * - Supports continuous or discrete (stepped) values
 * - Disabled state with grey styling
 *
 * @example
 * // Continuous slider (budget)
 * <Slider
 *   value={50}
 *   referenceValue={75}
 *   onChange={(v) => setBudget(v)}
 * />
 *
 * @example
 * // Discrete slider (voice 1-5)
 * <Slider
 *   value={50}
 *   referenceValue={25}
 *   step={25}
 *   onChange={(v) => setVoice(v)}
 * />
 */
export function Slider({
  value,
  referenceValue,
  onChange,
  disabled = false,
  step,
  onDragStart,
  onDragEnd,
}: SliderProps) {
  const [isDragging, setIsDragging] = useState(false);

  const showReferenceDot = referenceValue !== undefined && Math.abs(referenceValue - value) > 3;

  const handleDragStart = () => {
    setIsDragging(true);
    onDragStart?.();
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    onDragEnd?.();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    onChange(parseFloat(e.target.value));
  };

  return (
    <div className="relative h-6 flex items-center">
      {/* Reference value indicator (grey dot) - behind everything, only show when different */}
      {showReferenceDot && (
        <div
          className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-gray-200 rounded-full pointer-events-none z-0 ${isDragging ? '' : 'transition-all duration-300 ease-out'}`}
          style={{ left: `calc(${referenceValue}% - 8px)` }}
        />
      )}

      {/* Track background and fill - above reference dot */}
      <div className="absolute inset-x-0 h-2 bg-gray-200 rounded-full z-[1]">
        <div
          className={`absolute left-0 top-0 h-full rounded-full ${isDragging ? '' : 'transition-all duration-300 ease-out'} ${disabled ? 'bg-gray-400' : 'bg-[#290BD9]'}`}
          style={{ width: `${value}%` }}
        />
      </div>

      {/* Current value thumb - above track */}
      <div
        className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white border-2 rounded-full shadow-md pointer-events-none z-[2] ${isDragging ? '' : 'transition-all duration-300 ease-out'} ${disabled ? 'border-gray-400' : 'border-[#290BD9]'}`}
        style={{ left: `calc(${value}% - 8px)` }}
      />

      {/* Invisible range input - on top for interaction */}
      <input
        type="range"
        min="0"
        max="100"
        step={step}
        value={value}
        onChange={handleChange}
        onMouseDown={handleDragStart}
        onMouseUp={handleDragEnd}
        onTouchStart={handleDragStart}
        onTouchEnd={handleDragEnd}
        disabled={disabled}
        tabIndex={-1}
        className="absolute inset-x-0 h-6 w-full opacity-0 cursor-pointer disabled:cursor-not-allowed z-[3]"
      />
    </div>
  );
}
